import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { scrollToId } from "./Nav";
import { useLang } from "@/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1655635643486-a17bc48771ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMHRlY2glMjBwaW5rJTIwYmx1ZXxlbnwwfHx8fDE3ODcyNTA0NTV8MA&ixlib=rb-4.1.0&q=85";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } },
};

const lineVariant = {
  hidden: { y: "115%" },
  show: { y: 0, transition: { duration: 1.05, ease: [0.33, 1, 0.68, 1] } },
};

export default function Hero() {
  const { t, lang } = useLang();
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 140]);
  const yRing = useTransform(scrollY, [0, 700], [0, -110]);
  const opacityFade = useTransform(scrollY, [0, 500], [1, 0.15]);

  const lines = t("hero.lines");
  const accentIndex = t("hero.accentIndex");

  return (
    <section
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-28"
    >
      <div className="grid-lines absolute inset-0" aria-hidden="true" />
      <div
        className="absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-[#581820]/[0.05] blur-[140px]"
        aria-hidden="true"
      />
      <div
        className="absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-[#2D4A3E]/[0.05] blur-[140px]"
        aria-hidden="true"
      />

      <motion.div
        style={{ y: yRing, opacity: opacityFade }}
        className="pointer-events-none absolute right-[8%] top-[12%] hidden lg:block"
        aria-hidden="true"
      >
        <div className="relative h-[420px] w-[420px]">
          <div className="absolute inset-0 animate-spin-slow rounded-full border border-dashed border-neon-teal/30" />
          <div className="absolute inset-10 rounded-full border border-[#E6E2DC]" />
          <motion.div style={{ y: yImg }} className="absolute inset-20 overflow-hidden">
            <img
              src={HERO_IMG}
              alt="Abstract digital structure"
              className="h-full w-full object-cover opacity-80"
              style={{ clipPath: "polygon(0 0, 100% 8%, 92% 100%, 8% 92%)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-neon-teal/25 via-transparent to-neon-pink/25 mix-blend-overlay" />
          </motion.div>
          <span className="absolute -left-3 top-1/2 h-2 w-2 bg-neon-green" />
          <span className="absolute right-6 top-6 h-2 w-2 bg-neon-pink" />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-neon-teal"
          data-testid="hero-eyebrow"
        >
          <MapPin size={14} />
          {t("hero.eyebrow")}
        </motion.div>

        <motion.h1
          key={lang}
          data-testid="hero-heading"
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display text-[11vw] font-medium leading-[1.04] tracking-tight sm:text-[9vw] lg:text-[6.2vw]"
        >
          {lines.map((text, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                variants={lineVariant}
                className={`block ${i === accentIndex ? "italic text-[#581820]" : "text-[#141210]"}`}
              >
                {text}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="mt-12 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-md text-base leading-relaxed text-[#6B655F] md:text-lg"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollToId("services")}
              className="bg-[#141210] px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-neon-teal"
            >
              {t("hero.cta1")}
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToId("contact")}
              className="border border-[#141210] px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-[#141210] transition-colors duration-300 hover:border-neon-teal hover:text-neon-teal"
            >
              {t("hero.cta2")}
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        style={{ opacity: opacityFade }}
        className="absolute bottom-8 left-6 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-[#6B655F] transition-colors duration-300 hover:text-neon-teal md:left-12"
      >
        <ArrowDown size={14} className="animate-bounce" />
        {t("hero.scroll")}
      </motion.button>
    </section>
  );
}
